import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import Header from './components/Header';
import RankingsPage from './components/RankingsPage';
import StandingsPage from './components/StandingsPage';
import LeadersPage from './components/LeadersPage';
import './styles/app.css';
import './styles/mobile-readiness-2026.css';

const ROOT_FEATURE_VIEWS = new Set(['rankings', 'standings', 'leaders']);

function readRootView() {
  if (typeof window === 'undefined') {
    return 'app';
  }

  const hash = window.location.hash.replace('#', '').trim().toLowerCase();
  return ROOT_FEATURE_VIEWS.has(hash) ? hash : 'app';
}

function normalizeLegacyUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('sport')) return;
  url.searchParams.delete('sport');
  window.history.replaceState({}, '', url.toString());
}

function updateUrlForView(view) {
  const url = new URL(window.location.href);
  url.searchParams.delete('sport');
  url.searchParams.delete('match');
  url.searchParams.delete('team');
  url.searchParams.delete('teamSeason');
  url.searchParams.delete('rankingSeason');
  url.searchParams.delete('standingsConference');
  url.searchParams.delete('leadersType');
  url.searchParams.delete('leadersCategory');
  url.hash = view;
  window.history.pushState({}, '', url.toString());
}

function FeatureFooter() {
  return (
    <footer className="site-footer">
      <div className="shell">
        <strong>VolleyWire</strong>
        <span>The Home of College Volleyball</span>
        <small>Independent college volleyball coverage.</small>
      </div>
    </footer>
  );
}

function leaveFeatureView(view, onExitFeature) {
  const destination = view === 'teams' ? 'teams' : 'scores';
  updateUrlForView(destination);
  onExitFeature();
}

function openTeamProfile({ team, season = null, onExitFeature }) {
  const slug = String(team?.slug || '').trim();

  if (!slug) {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.delete('sport');
  url.searchParams.set('team', slug);

  if (season !== null && Number.isInteger(Number(season)) && Number(season) > 0) {
    url.searchParams.set('teamSeason', String(season));
  } else {
    url.searchParams.delete('teamSeason');
  }

  url.searchParams.delete('match');
  url.searchParams.delete('rankingSeason');
  url.searchParams.delete('standingsConference');
  url.searchParams.delete('leadersType');
  url.searchParams.delete('leadersCategory');
  url.hash = 'teams';
  window.history.pushState({}, '', url.toString());
  onExitFeature();
}

function RankingsShell({ onExitFeature }) {
  return (
    <div className="app-frame">
      <Header
        activeView="rankings"
        onNavigate={(view) => leaveFeatureView(view, onExitFeature)}
      />
      <RankingsPage
        onOpenTeam={(team, season) =>
          openTeamProfile({ team, season, onExitFeature })
        }
      />
      <FeatureFooter />
    </div>
  );
}

function StandingsShell({ onExitFeature }) {
  return (
    <div className="app-frame">
      <Header
        activeView="standings"
        onNavigate={(view) => leaveFeatureView(view, onExitFeature)}
      />
      <StandingsPage
        onOpenTeam={(team) => openTeamProfile({ team, onExitFeature })}
      />
      <FeatureFooter />
    </div>
  );
}

function LeadersShell({ onExitFeature }) {
  return (
    <div className="app-frame">
      <Header
        activeView="leaders"
        onNavigate={(view) => leaveFeatureView(view, onExitFeature)}
      />
      <LeadersPage
        onOpenTeam={(team) => openTeamProfile({ team, onExitFeature })}
      />
      <FeatureFooter />
    </div>
  );
}

function Root() {
  const [rootView, setRootView] = useState(readRootView);

  useEffect(() => {
    normalizeLegacyUrl();
  }, []);

  useEffect(() => {
    function handleNavigation() {
      setRootView(readRootView());
    }

    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('hashchange', handleNavigation);
    window.addEventListener('volleywire:navigation', handleNavigation);
    window.addEventListener('pageshow', handleNavigation);

    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('hashchange', handleNavigation);
      window.removeEventListener('volleywire:navigation', handleNavigation);
      window.removeEventListener('pageshow', handleNavigation);
    };
  }, []);

  useEffect(() => {
    if (rootView === 'rankings') {
      document.title = 'Rankings | VolleyWire';
    } else if (rootView === 'standings') {
      document.title = 'Standings | VolleyWire';
    } else if (rootView === 'leaders') {
      document.title = 'Leaders | VolleyWire';
    }
  }, [rootView]);

  if (rootView === 'rankings') {
    return <RankingsShell onExitFeature={() => setRootView('app')} />;
  }

  if (rootView === 'standings') {
    return <StandingsShell onExitFeature={() => setRootView('app')} />;
  }

  if (rootView === 'leaders') {
    return <LeadersShell onExitFeature={() => setRootView('app')} />;
  }

  return <App />;
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);
